import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Briefcase,
  FileText,
  CheckCircle2,
  XCircle,
  User,
  Users,
  MessageSquare,
  Shield,
  Eye,
  EyeOff,
  Loader2,
  ArrowLeft,
  AlertTriangle,
  FolderOpen,
  UserCheck,
  Calendar,
  Receipt,
  ChevronRight,
  Scale,
  Building2,
  Clock
} from 'lucide-react';
import { ReviewerPanel } from './GovernanceViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custody location badge
const CustodyBadge = ({ location }) => {
  const config = {
    held_by_cps: { label: 'Held by CPS', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    held_by_advocate: { label: 'Held by Advocate', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    shared_under_scoped_access: { label: 'Shared (Scoped)', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    restricted_to_cps_internal_review: { label: 'CPS Internal', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' }
  };
  
  const { label, color } = config[location] || { label: location, color: 'bg-slate-500/20 text-slate-400' };
  
  return (
    <Badge variant="outline" className={`text-xs ${color}`}>
      {label}
    </Badge>
  );
};

// Validation status badge
const ValidationBadge = ({ status }) => {
  const config = {
    pending_review: { label: 'Pending Review', color: 'bg-amber-500', icon: Clock },
    eligibility_verified: { label: 'Eligibility Verified', color: 'bg-blue-500', icon: CheckCircle2 },
    scope_approved: { label: 'Scope Approved', color: 'bg-emerald-500', icon: CheckCircle2 },
    scope_restricted: { label: 'Scope Restricted', color: 'bg-amber-500', icon: AlertTriangle },
    validation_expired: { label: 'Validation Expired', color: 'bg-rose-500', icon: XCircle }
  };
  
  const { label, color, icon: Icon } = config[status] || config.pending_review;
  
  return (
    <Badge className={`${color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// Bundle card
const BundleCard = ({ bundle }) => (
  <div className="p-4 rounded-lg border bg-slate-800/50 border-slate-700">
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        <FolderOpen className="w-4 h-4 text-indigo-400" />
        <span className="font-medium text-white">{bundle.title}</span>
      </div>
      <CustodyBadge location={bundle.custody_location} />
    </div>
    
    <div className="text-sm text-slate-400 mb-2">{bundle.purpose}</div>
    <div className="text-xs text-slate-500 mb-3">Custody: {bundle.custody_holder}</div>
    
    <div className="space-y-2">
      <div>
        <div className="text-xs text-emerald-400 mb-1 flex items-center gap-1">
          <Eye className="w-3 h-3" /> Current access:
        </div>
        <div className="flex flex-wrap gap-1">
          {bundle.current_access?.map((actor, i) => (
            <Badge key={`access-${i}`} variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
              {actor}
            </Badge>
          ))}
        </div>
      </div>
      
      {bundle.excluded_materials?.length > 0 && (
        <div>
          <div className="text-xs text-rose-400 mb-1 flex items-center gap-1">
            <EyeOff className="w-3 h-3" /> Excluded:
          </div>
          <div className="flex flex-wrap gap-1">
            {bundle.excluded_materials.map((item, i) => (
              <Badge key={`excluded-${i}`} variant="outline" className="text-xs border-rose-500/30 text-rose-400">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Actor access card
const ActorAccessCard = ({ grant }) => (
  <div className="p-4 rounded-lg border bg-slate-800/50 border-slate-700">
    <div className="flex items-center gap-2 mb-3">
      <UserCheck className="w-4 h-4 text-indigo-400" />
      <span className="font-medium text-white">{grant.actor_name}</span>
    </div>
    
    <div className="text-xs text-slate-500 mb-2">Purpose: {grant.purpose}</div>
    <div className="text-xs text-slate-600 mb-3">Authority: {grant.authority_basis}</div>
    
    <div className="space-y-2">
      <div>
        <div className="text-xs text-emerald-400 mb-1 flex items-center gap-1">
          <Eye className="w-3 h-3" /> May access:
        </div>
        <div className="flex flex-wrap gap-1">
          {grant.bundles_accessible?.map((item, i) => (
            <Badge key={`accessible-${i}`} variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
              {item}
            </Badge>
          ))}
        </div>
      </div>
      
      {grant.bundles_excluded?.length > 0 && (
        <div>
          <div className="text-xs text-rose-400 mb-1 flex items-center gap-1">
            <EyeOff className="w-3 h-3" /> Excluded:
          </div>
          <div className="flex flex-wrap gap-1">
            {grant.bundles_excluded.map((item, i) => (
              <Badge key={`excluded-${i}`} variant="outline" className="text-xs border-rose-500/30 text-rose-400">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Receipt pack entry
const ReceiptPackEntry = ({ entry, index }) => (
  <div className="flex gap-3 pb-4 border-b border-slate-700/50 last:border-0 last:pb-0">
    <div className="flex flex-col items-center">
      <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-sm font-medium">
        {index + 1}
      </div>
      {index < 6 && <div className="w-px h-full bg-slate-700 mt-2" />}
    </div>
    <div className="flex-1 pt-1">
      <div className="font-medium text-white mb-1">{entry.action}</div>
      <div className="text-sm text-slate-400 mb-2">
        {entry.actor} — {entry.outcome}
      </div>
      
      {entry.data_involved?.length > 0 && (
        <div className="mb-2">
          <div className="text-xs text-slate-500 mb-1">Involved:</div>
          <div className="flex flex-wrap gap-1">
            {entry.data_involved.map((item, i) => (
              <Badge key={`involved-${i}`} variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {entry.data_withheld?.length > 0 && (
        <div className="mb-2">
          <div className="text-xs text-slate-500 mb-1">Withheld:</div>
          <div className="flex flex-wrap gap-1">
            {entry.data_withheld.map((item, i) => (
              <Badge key={`withheld-${i}`} variant="outline" className="text-xs border-slate-600 text-slate-500">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      <div className="text-xs text-slate-600 font-mono">
        {entry.receipt_id} • {entry.case_episode_ref}
      </div>
    </div>
  </div>
);

// Message bubble
const MessageBubble = ({ message }) => {
  const isAlert = message.content?.includes('conflict') || message.content?.includes('Conflict');

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%]">
        <div className={`rounded-2xl px-4 py-3 ${
          isAlert
            ? 'bg-amber-500/20 text-amber-100 border border-amber-500/30 rounded-bl-sm'
            : 'bg-slate-800 text-white rounded-bl-sm'
        }`}>
          <div className={`text-xs mb-1 ${isAlert ? 'text-amber-400' : 'text-slate-400'}`}>
            {message.sender}
          </div>
          <div className="text-sm">{message.content}</div>
        </div>
        {message.governance_note && (
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
            <Shield className="w-3 h-3" />
            {message.governance_note}
          </div>
        )}
      </div>
    </div>
  );
};

// Advocate profile card
const AdvocateProfileCard = ({ profile }) => (
  <div className="p-4 rounded-lg border bg-slate-800/50 border-slate-700">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
          <User className="w-6 h-6 text-indigo-400" />
        </div>
        <div>
          <div className="font-semibold text-white">{profile?.name}</div>
          <div className="text-sm text-slate-400">{profile?.role}</div>
        </div>
      </div>
      <ValidationBadge status={profile?.validation_status} />
    </div>
    
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <div className="text-slate-500 text-xs">Bar Registration</div>
        <div className="text-white">{profile?.bar_registration}</div>
      </div>
      <div>
        <div className="text-slate-500 text-xs">Chambers</div>
        <div className="text-white">{profile?.chambers}</div>
      </div>
      <div className="col-span-2">
        <div className="text-slate-500 text-xs mb-1">Practice Areas</div>
        <div className="flex flex-wrap gap-1">
          {profile?.practice_areas?.map((area, i) => (
            <Badge key={`area-${i}`} variant="outline" className="text-xs border-slate-600 text-slate-400">
              {area}
            </Badge>
          ))}
        </div>
      </div>
      <div className="col-span-2">
        <div className="text-slate-500 text-xs">Validated by</div>
        <div className="text-white">{profile?.validated_by}</div>
      </div>
    </div>
  </div>
);

// Allocation card
const AllocationCard = ({ allocation }) => (
  <div className={`p-4 rounded-lg border ${
    allocation?.conflict_flags?.length > 0 
      ? 'bg-amber-500/10 border-amber-500/30' 
      : 'bg-emerald-500/10 border-emerald-500/30'
  }`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-indigo-400" />
        <span className="font-medium text-white">Court Allocation</span>
      </div>
      <Badge className={allocation?.conflict_flags?.length > 0 ? 'bg-amber-500' : 'bg-emerald-500'}>
        {allocation?.status}
      </Badge>
    </div>
    
    <div className="space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-slate-400">Court Session</span>
        <span className="text-white">{allocation?.court_session}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-slate-400">Assigned Advocate</span>
        <span className="text-white">{allocation?.assigned_advocate}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-slate-400">Assigned By</span>
        <span className="text-white">{allocation?.assigned_by}</span>
      </div>
    </div>
    
    {allocation?.conflict_flags?.length > 0 && (
      <div className="mt-3 pt-3 border-t border-amber-500/30">
        <div className="flex items-center gap-1 text-xs text-amber-400 mb-1">
          <AlertTriangle className="w-3 h-3" /> Conflict Flags:
        </div>
        {allocation.conflict_flags.map((flag, i) => (
          <div key={`flag-${i}`} className="text-xs text-amber-300">{flag}</div>
        ))}
        {allocation.notes && (
          <div className="text-xs text-slate-400 mt-2">{allocation.notes}</div>
        )}
      </div>
    )}
  </div>
);

// Receipt Pack Modal
const ReceiptPackModal = ({ isOpen, onClose, receiptPack, caseReference }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-indigo-400" />
            <span className="font-semibold text-white">Evidence Pack: {caseReference}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
            ✕
          </Button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div className="mb-4 p-3 bg-slate-800/50 rounded-lg">
            <div className="text-sm text-slate-400 mb-2">
              This evidence pack documents each governed action. Every material handoff is attributable and reviewable.
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="text-center p-2 bg-indigo-500/10 rounded">
                <div className="text-indigo-400 font-medium">{receiptPack?.length || 0}</div>
                <div className="text-slate-500">Total Actions</div>
              </div>
              <div className="text-center p-2 bg-emerald-500/10 rounded">
                <div className="text-emerald-400 font-medium">3</div>
                <div className="text-slate-500">Episodes</div>
              </div>
              <div className="text-center p-2 bg-slate-700 rounded">
                <div className="text-white font-medium">4</div>
                <div className="text-slate-500">Actors</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            {receiptPack?.map((entry, i) => (
              <ReceiptPackEntry key={entry.receipt_id} entry={entry} index={i} />
            ))}
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-700 bg-slate-800/30">
          <div className="text-xs text-slate-500 text-center">
            All actions recorded with case episode references. Full audit trail available on request.
          </div>
        </div>
      </div>
    </div>
  );
};

const CPSDemo = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showReviewer, setShowReviewer] = useState(false);
  const [showReceiptPack, setShowReceiptPack] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadDemo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/governance/cps/demo`);
        if (res.ok) {
          const json = await res.json();
          setData(json);
        } else {
          setLoadError('Failed to load CPS demo');
        }
      } catch (err) {
        setLoadError('Failed to load CPS demo');
      } finally {
        setLoading(false);
      }
    };
    loadDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center text-red-400">{loadError}</div>
      </div>
    );
  }

  const { case_reference, case_title, advocate_profile, bundles, actor_access_grants, allocation_record, receipt_pack, messages, workflow } = data || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/demos" className="text-slate-400 hover:text-white" data-testid="back-to-demos">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-indigo-400" />
              <span className="font-semibold text-white">Brief & Advocate Allocation</span>
            </div>
            <Badge variant="outline" className="border-indigo-500/50 text-indigo-400">
              CPS Demo
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowReceiptPack(true)}
              className="border-slate-600 text-slate-400 hover:text-white"
              data-testid="view-receipt-pack-btn"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Evidence Pack
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowReviewer(!showReviewer)}
              className={`border-slate-600 ${showReviewer ? 'bg-indigo-600 text-white border-indigo-600' : 'text-slate-400'}`}
              data-testid="toggle-governance-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              {showReviewer ? 'Hide Governance' : 'Show Governance'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className={`grid gap-6 ${showReviewer ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}>
          
          {/* Main Panel */}
          <div className={showReviewer ? 'lg:col-span-1' : 'lg:col-span-2'}>
            <div className="space-y-4">
              
              {/* Case Header */}
              <Card className="bg-indigo-500/10 border-indigo-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-6 h-6 text-indigo-400" />
                    <div>
                      <div className="text-white font-medium">{case_title}</div>
                      <div className="text-sm text-indigo-400">{case_reference}</div>
                    </div>
                    <Badge className="ml-auto bg-emerald-500">{workflow?.status?.split(',')[0]}</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Tab Navigation */}
              <div className="flex gap-1 p-1 bg-slate-800/50 rounded-lg overflow-x-auto">
                {[
                  { id: 'overview', label: 'Overview', icon: Briefcase },
                  { id: 'validation', label: 'Validation', icon: UserCheck },
                  { id: 'briefs', label: 'Brief Sharing', icon: FileText },
                  { id: 'allocation', label: 'Allocation', icon: Calendar },
                  { id: 'access', label: 'Who Sees What', icon: Users },
                  { id: 'messages', label: 'Updates', icon: MessageSquare }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 py-2 px-3 rounded-md text-xs whitespace-nowrap transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-indigo-600 text-white' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-3.5 h-3.5" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base">Three Governed Episodes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          <div>
                            <div className="text-white font-medium">1. Advocate Validated</div>
                            <div className="text-xs text-slate-400">Credentials reviewed, scope approved by Reviewing Manager</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                          <div>
                            <div className="text-white font-medium">2. Brief Shared</div>
                            <div className="text-xs text-slate-400">Scoped access granted, exclusions visible</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                          <CheckCircle2 className="w-5 h-5 text-amber-400" />
                          <div>
                            <div className="text-white font-medium">3. Allocation Confirmed</div>
                            <div className="text-xs text-slate-400">Update attributed, conflict acknowledged</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-slate-800/30 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-slate-700 rounded-lg">
                          <Eye className="w-4 h-4 text-slate-400" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white mb-1">What's been shared</div>
                          <div className="text-sm text-slate-400">{workflow?.shared_with_summary}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Validation Tab */}
              {activeTab === 'validation' && (
                <div className="space-y-4">
                  <AdvocateProfileCard profile={advocate_profile} />
                  
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base">Validation Timeline</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {receipt_pack?.slice(0, 3).map((entry, i) => (
                          <div key={entry.receipt_id} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-medium">
                              {i + 1}
                            </div>
                            <div>
                              <div className="text-sm text-white">{entry.action}</div>
                              <div className="text-xs text-slate-400">{entry.actor} — {entry.outcome}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Brief Sharing Tab */}
              {activeTab === 'briefs' && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <FolderOpen className="w-4 h-4 text-indigo-400" />
                      Document Bundles
                      <Badge variant="outline" className="ml-auto border-slate-600 text-slate-400">
                        {bundles?.length || 0} bundles
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-400 mb-4">
                      Scoped bundles with custody, access, and exclusions clearly shown.
                    </div>
                    <div className="space-y-3" data-testid="bundles-list">
                      {bundles?.map(bundle => (
                        <BundleCard key={bundle.bundle_id} bundle={bundle} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Allocation Tab */}
              {activeTab === 'allocation' && (
                <div className="space-y-4">
                  <AllocationCard allocation={allocation_record} />
                  
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base">Allocation Update Trail</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {receipt_pack?.slice(5, 7).map((entry, i) => (
                          <div key={entry.receipt_id} className="flex items-start gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                              i === 1 ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'
                            }`}>
                              {i + 1}
                            </div>
                            <div>
                              <div className="text-sm text-white">{entry.action}</div>
                              <div className="text-xs text-slate-400">{entry.actor} — {entry.outcome}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Who Sees What Tab */}
              {activeTab === 'access' && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Users className="w-4 h-4 text-indigo-400" />
                      Who Can See What
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-400 mb-4">
                      Different actors have access to different bundles based on role and purpose.
                    </div>
                    <div className="space-y-3" data-testid="actor-access-grants">
                      {actor_access_grants?.map((grant, i) => (
                        <ActorAccessCard key={`grant-${i}`} grant={grant} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-indigo-400" />
                      Update Thread
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3" data-testid="messages-list">
                      {messages?.map(msg => (
                        <MessageBubble key={msg.message_id} message={msg} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Side Panel */}
          {showReviewer ? (
            <div className="lg:col-span-1">
              <Card className="bg-slate-800/50 border-slate-700 sticky top-20">
                <CardContent className="p-4">
                  <ReviewerPanel workflow={workflow} title="Governance: CPS Workflow" />
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Key Stats */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base">Governance Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-indigo-500/10 text-center">
                      <div className="text-2xl font-bold text-indigo-400">{bundles?.length || 0}</div>
                      <div className="text-xs text-slate-400">Bundles</div>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-500/10 text-center">
                      <div className="text-2xl font-bold text-emerald-400">{receipt_pack?.length || 0}</div>
                      <div className="text-xs text-slate-400">Receipts</div>
                    </div>
                    <div className="p-3 rounded-lg bg-purple-500/10 text-center">
                      <div className="text-2xl font-bold text-purple-400">{actor_access_grants?.length || 0}</div>
                      <div className="text-xs text-slate-400">Actors</div>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-500/10 text-center">
                      <div className="text-2xl font-bold text-amber-400">3</div>
                      <div className="text-xs text-slate-400">Episodes</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-base">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-between border-slate-600 text-slate-300 hover:text-white"
                      onClick={() => setShowReceiptPack(true)}
                      data-testid="quick-receipt-pack-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Receipt className="w-4 h-4" />
                        View Evidence Pack
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-between border-slate-600 text-slate-300 hover:text-white"
                      onClick={() => setShowReviewer(true)}
                      data-testid="quick-governance-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        View Governance Trail
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Pack Modal */}
      <ReceiptPackModal 
        isOpen={showReceiptPack} 
        onClose={() => setShowReceiptPack(false)} 
        receiptPack={receipt_pack}
        caseReference={case_reference}
      />
    </div>
  );
};

export default CPSDemo;
